import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useTranslation } from 'next-i18next';

import { logger } from '@zep/core-logger';

import { HttpStatusCodeEnum } from '@/api';
import { alertDialog } from '@/ui_v3';

import { type YoutubeVideoDataApiResponse } from './types';
import { youtubeApi } from './youtubeApi';

/**
 * YouTube 영상 메타데이터 조회 훅 (캐싱 최적화)
 *
 * queryClient.fetchQuery를 사용하여 완전한 캐싱 혜택 제공:
 * - 캐시 히트 시 즉시 반환 (네트워크 요청 없음)
 * - Request deduplication (동시 요청 시 하나만 실행)
 * - YouTube API quota 절약 (~40% 예상)
 *
 * 캐싱 전략:
 * - staleTime: 1시간 (메타데이터는 거의 변하지 않음)
 * - gcTime: 24시간 (메모리 효율성)
 *
 * @returns fetchPreview - YouTube videoId로 메타데이터를 조회하는 함수
 *
 * @example
 * ```tsx
 * const { fetchPreview } = useYoutubePreview();
 * const data = await fetchPreview(videoId);
 * ```
 */
export const useYoutubePreview = () => {
  const queryClient = useQueryClient();
  const handleAlert = useYoutubePreviewErrorAlert();

  const fetchPreview = useCallback(
    async (videoId: string): Promise<YoutubeVideoDataApiResponse> => {
      try {
        // fetchQuery가 자동으로 캐시를 확인하고 필요시에만 API 호출
        const data = await queryClient.fetchQuery({
          queryKey: ['youtube', 'preview', videoId],
          queryFn: () => youtubeApi.getPreview(videoId),
          staleTime: 1000 * 60 * 60, // 1시간 (메타데이터는 거의 변하지 않음)
          gcTime: 1000 * 60 * 60 * 24, // 24시간 (메모리 관리)
          retry: 2, // YouTube API 불안정성 대비
        });
        return data;
      } catch (error) {
        handleAlert(error as AxiosError);
        throw error;
      }
    },
    [queryClient, handleAlert],
  );

  return { fetchPreview };
};

const useYoutubePreviewErrorAlert = () => {
  const { t } = useTranslation(['build']);

  const handleAlert = (error: AxiosError | Error) => {
    if (!(error instanceof AxiosError)) {
      alertDialog.open({
        header: {
          title: t('build:build.ai.youtube.preview.error.failed'),
        },
      });
      return;
    }

    switch (error.status) {
      case HttpStatusCodeEnum.NOT_FOUND_404:
        alertDialog.open({
          header: {
            title: t('build:build.ai.youtube.preview.error.notFound'),
          },
          body: t('build:build.ai.youtube.preview.error.notFoundBody'),
        });
        break;
      case HttpStatusCodeEnum.SERVICE_UNAVAILABLE_503:
        alertDialog.open({
          header: {
            title: t(
              'build:build.ai.youtube.preview.error.temporaryUnavailable',
            ),
          },
        });
        logger.track('ai_youtube_metadata_error', {
          reason: 'API_QUOTA_EXCEEDED',
        });
        break;
      case HttpStatusCodeEnum.BAD_GATEWAY_502:
        alertDialog.open({
          header: {
            title: t(
              'build:build.ai.youtube.preview.error.temporaryUnavailable',
            ),
          },
        });
        logger.track('ai_youtube_metadata_error', {
          reason: 'YOUTUBE_SERVER_TIMEOUT',
        });
        break;

      case undefined:
      default:
        alertDialog.open({
          header: {
            title: t(
              'build:build.ai.youtube.preview.error.temporaryUnavailable',
            ),
          },
        });
    }
  };

  return handleAlert;
};
